import React, { Ref, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CreateContractWizard } from "../helpers/helpers";

import { convertToStringForDocuments } from "../../FileUpload";
import { Grid, TextInput } from "@mantine/core";
import Dropzone from "@/components/molecules/Dropzone";
import {
  IMAGE_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
  MS_POWERPOINT_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  PDF_MIME_TYPE,
} from "@mantine/dropzone";
import { useAuth } from "@/contexts/AuthContext";
import DocumentTable from "@/components/organisms/DocumentTable";
import Divider from "@/components/atoms/Divider";
import Text from "@/components/atoms/Text";
import { ORDER_DRAWER_DOCUMENTS_COLUMN } from "@/components/organisms/DocumentTable/const/order-drawer";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: Partial<CreateContractWizard>) => void;
  wizardData: CreateContractWizard;
  ref?: Ref<HTMLFormElement>;
}

function Attachments({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
    const [files, setFiles] = React.useState<File[]>([]);
    const { t } = useTranslation();
    const { currentCompany } = useAuth();

    const onChangeFiles = async (files: File[]) => {
      const convertedFiles = await convertToStringForDocuments(files);

      return onChangeWizardData({
        attachments: convertedFiles.map((file) => ({
          ...file,
          languageId: currentCompany?.companyLanguageId as number,
          companyIds: [wizardData.tradingPartner, currentCompany?.companyId] as number[],
          documentType: "EXPORT_DOCUMENT",
        })),
      });
    };

    useEffect(() => {
      if (files && files.length > 0) {
        onChangeFiles(files).then();
      }
    }, [files]);

    return (
      <>
        <form
          ref={ref}
          onSubmit={(e) => {
            // e.preventDefault() is necessary to prevent the form from submitting and refreshing the page when the user clicks the next button. The onValidationSuccess function will be called to handle the form submission logic and move to the next step in the wizard.
            e.preventDefault();
            onValidationSuccess(wizardData);
          }}
          data-testid={"contract-attachments-form"}
        >
          <Grid>
            <Grid.Col span={12}>
              <Text mb={4} data-testid="pick-from-previously-uploaded">
                {t("actions:pickFromPreviouslyUploaded")}
              </Text>
              <Divider />
              <DocumentTable
                selectable={true}
                actionable={false}
                selectedRows={wizardData.attachmentUUIDs || []}
                setSelectedRows={(rows: string[]) => onChangeWizardData({ attachmentUUIDs: rows })}
                visibleColumns={ORDER_DRAWER_DOCUMENTS_COLUMN}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Text mb={4} data-testid="upload-from-local">
                {t("actions:uploadFromLocal")}
              </Text>
              <Divider />
              <Dropzone
                origin="local"
                type={"button"}
                files={files}
                setFiles={setFiles}
                buttonPosition="left"
                showAsImageCarousel={false}
                buttonLabel={t("actions:uploadAttachments")}
                accept={[
                  ...IMAGE_MIME_TYPE,
                  ...PDF_MIME_TYPE,
                  ...MS_WORD_MIME_TYPE,
                  ...MS_EXCEL_MIME_TYPE,
                  ...MS_POWERPOINT_MIME_TYPE,
                ]}
              />
            </Grid.Col>
            <Grid.Col span={12}></Grid.Col>
            <Grid.Col span={10}>
              <TextInput
                id="create-contract-comments"
                data-testid="create-contract-comments"
                label={t("contracts:moreComments")}
                value={wizardData.comment}
                onChange={(e: any) => onChangeWizardData({ comment: e.target.value })}
              />
            </Grid.Col>
          </Grid>
        </form>
      </>
    );
}

export default Attachments;
